import { motion } from 'framer-motion';
import { TrendingUp } from '../icons';
import type { MonthlyGrowth } from '../../lib/admin-dashboard';

interface GrowthChartProps {
  data: MonthlyGrowth[];
}

export function GrowthChart({ data }: GrowthChartProps) {
  const maxUsers = Math.max(...data.map(d => d.users), 1);
  const maxAssessments = Math.max(...data.map(d => d.assessments), 1);
  const maxValue = Math.max(maxUsers, maxAssessments);

  const totalUsers = data.reduce((sum, d) => sum + d.users, 0);
  const totalAssessments = data.reduce((sum, d) => sum + d.assessments, 0);

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-success-40" />
          <h3 className="text-text-xl text-gray-80">Platform Growth</h3>
        </div>
        <div className="flex items-center gap-4 text-text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-brand-40" />
            <span className="text-gray-50">Registrations ({totalUsers})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-success-40" />
            <span className="text-gray-50">Assessments ({totalAssessments})</span>
          </div>
        </div>
      </div>

      {totalUsers === 0 && totalAssessments === 0 ? (
        <p className="text-text-sm text-gray-40 text-center py-8">
          No growth data available for the last 6 months
        </p>
      ) : (
        <div className="flex items-end gap-3 h-44">
          {data.map((month, index) => (
            <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="flex items-end gap-1 w-full justify-center" style={{ height: '130px' }}>
                <motion.div
                  className="flex-1 bg-brand-40 rounded-t-sm min-h-[3px] max-w-[16px]"
                  initial={{ height: 3 }}
                  animate={{
                    height: month.users > 0
                      ? `${Math.max((month.users / maxValue) * 120, 6)}px`
                      : '3px',
                  }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  style={{ opacity: month.users > 0 ? 1 : 0.2 }}
                />
                <motion.div
                  className="flex-1 bg-success-40 rounded-t-sm min-h-[3px] max-w-[16px]"
                  initial={{ height: 3 }}
                  animate={{
                    height: month.assessments > 0
                      ? `${Math.max((month.assessments / maxValue) * 120, 6)}px`
                      : '3px',
                  }}
                  transition={{ duration: 0.5, delay: index * 0.08 + 0.05 }}
                  style={{ opacity: month.assessments > 0 ? 1 : 0.2 }}
                />
              </div>
              <span className="text-text-xs text-gray-40 mt-1">{month.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
